import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";

// Select-only combobox: focus stays on the trigger while the active option is announced.
export default function DiscoverySelect({ label, value, options: allOptions, onChange, icon: Icon, disabled = false, compact = false, invalid, describedBy, searchable = false, multilineOptions = false }) {
  const id = useId();
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const listRef = useRef(null);
  const optionRefs = useRef([]);
  const searchRef = useRef(null);
  const [query, setQuery] = useState("");
  const options = searchable && query.trim() ? allOptions.filter(option => `${option.label} ${option.value}`.toLowerCase().includes(query.trim().toLowerCase())) : allOptions;
  const typed = useRef({ text: "", time: 0 });
  const selectedIndex = Math.max(0, allOptions.findIndex((option) => option.value === value));
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const [position, setPosition] = useState(null);
  const selected = allOptions[selectedIndex];
  const active = !disabled && !compact && Boolean(value) && !["all", "any"].includes(value);

  useLayoutEffect(() => { setOpen(false); setActiveIndex(selectedIndex); }, [value, disabled, selectedIndex]);

  useEffect(() => { if (open && position && searchable) searchRef.current?.focus({ preventScroll: true }); }, [open, position, searchable]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const placeMenu = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewLeft = viewport?.offsetLeft || 0;
      const viewTop = viewport?.offsetTop || 0;
      const viewWidth = viewport?.width || window.innerWidth;
      const viewHeight = viewport?.height || window.innerHeight;
      const width = Math.min(Math.max(rect.width, compact ? 184 : 224), viewWidth - 20);
      const desiredHeight = Math.min(340, options.length * (multilineOptions ? 72 : 46) + (searchable ? 110 : 48));
      const below = viewTop + viewHeight - rect.bottom - 18;
      const above = rect.top - viewTop - 18;
      const opensBelow = below >= Math.min(desiredHeight, 180) || below >= above;
      const maxHeight = Math.max(0, Math.min(desiredHeight, opensBelow ? below : above));
      const left = Math.max(viewLeft + 10, Math.min(compact ? rect.right - width : rect.left, viewLeft + viewWidth - width - 10));
      setPosition({ left, width, maxHeight, top: opensBelow ? rect.bottom + 8 : rect.top - maxHeight - 8 });
    };
    placeMenu();
    window.addEventListener("resize", placeMenu);
    window.visualViewport?.addEventListener("resize", placeMenu);
    window.visualViewport?.addEventListener("scroll", placeMenu);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.visualViewport?.removeEventListener("resize", placeMenu);
      window.visualViewport?.removeEventListener("scroll", placeMenu);
    };
  }, [open, compact, options.length, searchable, multilineOptions]);

  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event) => {
      if (!triggerRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss, true);
    return () => document.removeEventListener("pointerdown", dismiss, true);
  }, [open]);

  useLayoutEffect(() => {
    const list = listRef.current;
    const option = optionRefs.current[activeIndex];
    if (!open || !list || !option) return;
    // Scroll only the menu, never the document or the fixed map.
    if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop;
    else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight;
    }
  }, [open, activeIndex, position]);

  function openAt(index = selectedIndex) {
    if (disabled || !allOptions.length) return;
    setQuery("");
    setActiveIndex(index);
    setPosition(null);
    setOpen(true);
  }

  function choose(index, restoreFocus = true) {
    const option = options[index];
    setOpen(false);
    typed.current = { text: "", time: 0 };
    if (option && option.value !== value) onChange(option.value);
    if (restoreFocus) triggerRef.current?.focus({ preventScroll: true });
  }

  function handleKeyDown(event) {
    if (disabled || event.ctrlKey || event.metaKey) return;
    const { key } = event;
    if (event.target === searchRef.current && !["ArrowDown", "ArrowUp", "Enter", "Escape", "Tab"].includes(key)) return;
    if (key === "Escape") {
      if (open) { event.preventDefault(); event.stopPropagation(); setOpen(false); triggerRef.current?.focus({ preventScroll: true }); }
      typed.current = { text: "", time: 0 };
      return;
    }
    if (key === "Tab") {
      if (open && searchable) { setOpen(false); triggerRef.current?.focus({ preventScroll: true }); }
      else if (open) choose(activeIndex, false);
      return;
    }
    if (event.altKey && key === "ArrowUp") {
      event.preventDefault();
      if (open) choose(activeIndex);
      return;
    }
    if (["Enter", " "].includes(key)) {
      event.preventDefault();
      if (open) choose(activeIndex);
      else openAt();
      return;
    }
    const moves = { ArrowDown: 1, ArrowUp: -1, PageDown: 10, PageUp: -10 };
    if (Object.hasOwn(moves, key) || key === "Home" || key === "End") {
      event.preventDefault();
      const index = key === "Home" ? 0 : key === "End" ? options.length - 1
        : open ? Math.max(0, Math.min(options.length - 1, activeIndex + moves[key]))
          : key === "ArrowUp" ? 0 : selectedIndex;
      if (open) setActiveIndex(index);
      else openAt(index);
      return;
    }
    if (key.length === 1 && !event.altKey) {
      event.preventDefault();
      const now = Date.now();
      const text = now - typed.current.time < 700 ? typed.current.text + key.toLowerCase() : key.toLowerCase();
      typed.current = { text, time: now };
      const repeated = [...text].every((character) => character === text[0]);
      const prefix = repeated ? text[0] : text;
      const start = repeated ? (open ? activeIndex : selectedIndex) + 1 : 0;
      for (let offset = 0; offset < options.length; offset += 1) {
        const index = (start + offset) % options.length;
        if (options[index].label.toLowerCase().startsWith(prefix)) {
          if (open) setActiveIndex(index);
          else openAt(index);
          break;
        }
      }
    }
  }

  return <>
    <button ref={triggerRef} type="button" role="combobox" aria-label={label} aria-invalid={invalid || undefined} aria-describedby={describedBy}
      aria-expanded={open} aria-haspopup="listbox" aria-controls={open ? `${id}-list` : undefined}
      aria-activedescendant={open && !searchable && options.length ? `${id}-option-${activeIndex}` : undefined}
      disabled={disabled} data-active={active}
      className={`discovery-select-trigger ${compact ? "discovery-sort" : "discovery-filter"}`}
      onKeyDown={handleKeyDown} onBlur={(event) => { if (event.relatedTarget && !menuRef.current?.contains(event.relatedTarget)) setOpen(false); }}
      onClick={() => { triggerRef.current?.focus({ preventScroll: true }); if (open) setOpen(false); else openAt(); }}>
      {Icon && <Icon className="discovery-select-icon" size={16} aria-hidden="true" />}
      <span className="discovery-select-value">{selected?.label || label}</span>
      <ChevronDown className="discovery-select-chevron" size={13} aria-hidden="true" />
    </button>
    {open && createPortal(<div ref={menuRef} className="discovery-select-menu" style={{ ...position, visibility: position ? "visible" : "hidden" }}>
      <div id={`${id}-label`} className="discovery-select-heading">{label}</div>
      {searchable && <input ref={searchRef} className="discovery-select-search" type="search" aria-label={`Search ${label.toLowerCase()}`} placeholder={`Search ${label.toLowerCase()}…`} value={query} aria-controls={`${id}-list`} aria-activedescendant={options.length ? `${id}-option-${activeIndex}` : undefined} onChange={event => { setQuery(event.target.value); setActiveIndex(0); }} onKeyDown={handleKeyDown} />}
      {!options.length && <p className="discovery-select-empty" role="status">No matching options</p>}
      <div id={`${id}-list`} ref={listRef} role="listbox" aria-labelledby={`${id}-label`} className="discovery-select-options">
        {options.map((option, index) => <div key={option.value} id={`${id}-option-${index}`}
          ref={(node) => { optionRefs.current[index] = node; }} role="option" aria-selected={option.value === value}
          data-highlighted={index === activeIndex} className="discovery-select-option"
          onPointerMove={() => setActiveIndex(index)} onMouseDown={(event) => event.preventDefault()}
          onClick={() => choose(index)}>
          <span>{option.label}</span>
          {option.value === value && <Check size={16} aria-hidden="true" />}
        </div>)}
      </div>
    </div>, triggerRef.current?.closest("dialog") || document.body)}
  </>;
}
